/**
 * Who is using the application, which tenant they are riding in, and which customer they are.
 *
 * All three are read from the access token and never chosen by the application. The scopes decide
 * what is drawn, the tenant decides what is asked for, and a client that let either be set locally
 * would be deciding its own authorization — which `apps/README.md` forbids and which every service
 * would refuse anyway.
 *
 * ## The one thing this file asserts that the driver application's refuses to
 *
 * `apps/driver-app/src/session.ts` will not treat the token's subject as a `driverId`, because R-10
 * measured that DRV-01 mints one with `randomUUID()` and keeps it separate from the identity
 * subject. **On the customer side the opposite is true and four services act on it**, which is the
 * whole of [ADR 0026](../../../docs/adr/0026-a-layer-with-no-subject-is-read-through-one-that-has.md)
 * section 3: MOB-03 mints a ride request's `passengerId` from the creating token's own subject,
 * TRP-01 and TRP-02 refuse a non-participant by comparing the subject to their own rider field,
 * COM-04 resolves a cart's customer from the reading token, and CUS-04 compares the subject to the
 * `customerId` in its own path. EXP-002 then refuses a `customerId` in its path that is not the
 * caller's own subject, with `customer_access_denied`.
 *
 * So `customerId` **is** `sub` here, and sending anything else would be refused at the edge rather
 * than silently answered. That equivalence is a precondition rather than a fact of nature — ADR 0026
 * records it as Q-56 — and the failure mode if IAM-01 ever mints a customer identifier distinct from
 * the Keycloak subject is loud: every journey screen 403s. That is the correct failure, and the fix
 * is the identity mapping rather than a fallback here.
 */

export interface Session {
  readonly subject: string;
  readonly displayName: string;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  /**
   * The customer this session is for, which is the token's own subject.
   *
   * A separate field rather than `subject` read twice, so every call site says which of the two
   * meanings it wants. ADR 0026 section 3 is the reason they are the same value and the reason the
   * identifier is still carried in the path rather than derived from the token by the service: "an
   * operation whose subject is implicit is an operation whose audit trail says whoever asked".
   */
  readonly customerId: string;
  /** What the design's own sample calls the rider — `Hi Amina` — when the token carries a name. */
  readonly givenName: string;
}

interface AccessTokenClaims {
  readonly sub?: unknown;
  readonly name?: unknown;
  readonly given_name?: unknown;
  readonly scope?: unknown;
  readonly tenant_id?: unknown;
}

/**
 * The first word of a name, which is what the design's chip greets.
 *
 * `Hi Amina` rather than `Hi Amina Mwangi`, because the chip is a 30px pill at 11.5px and the
 * design writes one word in it. A name with no space is used whole.
 */
function givenNameOf(displayName: string, claimed: string | undefined): string {
  if (claimed !== undefined && claimed.length > 0) return claimed;
  const first = displayName.split(/\s+/).find((word) => word.length > 0);
  return first ?? displayName;
}

/**
 * Read a session out of an access token, without trusting it for anything but display and
 * addressing.
 *
 * The signature is verified by the services, not here — a browser cannot hold the key, and a client
 * that "validated" a token would be performing a check whose failure it would also be responsible
 * for reporting. What this does is read the claims so the application knows which customer to ask
 * about and what it may ask for, and every request still carries the token to a service that checks
 * it properly.
 */
export function sessionFromClaims(claims: AccessTokenClaims): Session | undefined {
  const subject = typeof claims.sub === "string" ? claims.sub : undefined;
  const tenantId = typeof claims.tenant_id === "string" ? claims.tenant_id : undefined;
  if (subject === undefined || tenantId === undefined) return undefined;

  const scope = claims.scope;
  const scopes = Array.isArray(scope)
    ? scope.filter((value): value is string => typeof value === "string")
    : typeof scope === "string"
      ? scope.split(" ").filter((value) => value.length > 0)
      : [];

  const displayName =
    typeof claims.name === "string" && claims.name.length > 0 ? claims.name : subject;

  return {
    subject,
    displayName,
    scopes: new Set(scopes),
    tenantId,
    customerId: subject,
    givenName: givenNameOf(
      displayName,
      typeof claims.given_name === "string" ? claims.given_name : undefined,
    ),
  };
}

/**
 * Every scope a screen of this application depends on, named so a refusal can say which one.
 *
 * Nine, over eight services, and the spread is the point: this channel is not one BFF's client. The
 * journey screens read EXP-002 and nothing else, which is what `customer-app:read` buys; the three
 * screens before a ride request exists read MOB-01's listing and write MOB-02's and MOB-03's
 * commands, because EXP-002 deliberately serves none of them — ADR 0026 section 1 rejected the class
 * chooser on the ground that a BFF minting three quotes to draw one screen would be taking a domain
 * decision.
 *
 * `ride-request:self` rather than `ride-request:read` for the cancel: ADR 0028 split that operation
 * into two audiences on one path, and a rider is the owner rather than the operator.
 */
export const SCOPES = {
  /**
   * The three `/v1/me/` listings behind `Order history` — ADR 0096's convention.
   *
   * Each is the `:self` half of a pair whose other half reads across a tenant. A rider's token
   * carries only these, so the same screen signed in as an operator would read more and signed in
   * as a rider reads exactly their own rows — the bound is the token, not the request.
   */
  ownTrips: "trip:self",
  ownCommerceOrders: "commerce-order:self",
  ownDeliveryOrders: "delivery:order:self",
  /** EXP-002's three aggregates. The gate on every journey screen. */
  journey: "customer-app:read",
  /** MOB-01's product listing, which `2 Choose` draws its rows from. */
  products: "ride-product:read",
  /** MOB-02's price, which is a write because a quote is an immutable priced resource. */
  quote: "ride-quote:create",
  /** MOB-02's re-read of one, which is how `3 Quote` and `3b` learn whether it is still held. */
  validateQuote: "ride-quote:validate",
  /** MOB-03's booking command, pressed once by `3 Quote`'s Confirm ride. */
  book: "ride-request:create",
  /** ADR 0028's owner-side scope, for a rider's own read and their own cancel. */
  ownRequest: "ride-request:self",
  /**
   * DSP-04's dispatch process, which is the whole of `5 Finding` and `5b No driver`.
   *
   * Its own scope rather than `dispatch:orchestrate`, which is the decision ADR 0031 records: the
   * five fields these two screens draw were computed from the day DSP-04 was written and published
   * only inside the 202 of its event-ingestion `POST`, so the only caller that could read a
   * dispatch's progress was one entitled to drive the saga. A rider reads; a rider does not
   * orchestrate.
   */
  dispatchProcess: "dispatch:read",
  /** GEO-02's reverse geocode: the only way a coordinate becomes a place name. */
  reverseGeocode: "geo:reverse-geocode",
  /** GEO-01's forward geocode, behind `1 Where to`'s destination search. */
  geocode: "geocoding:query",
  /** GEO-04's distance and duration, which is `2 Choose`'s `24 km · about 38 min`. */
  distanceEta: "distance-eta:query",
  /** RAT-01's rating, submitted by `8 Complete`'s five stars. */
  rate: "rating:submit",
  /** SAF-01's incident, which is what the red SOS control raises. */
  safety: "safety:write",

  /* --------------------------------------------- the second tranche: commerce, money, account */

  /** MER-01's merchant listing, which is every row of `Merchants`. */
  merchants: "merchant:read",
  /** COM-01's catalogue, for the category name `Products · stock` heads its list with. */
  catalog: "catalog:read",
  /**
   * COM-02's product listing and its prices.
   *
   * Not `products`, which is already MOB-01's `ride-product:read` above. The two are different
   * services with the same English word for different things — a ride class and a bunch of sukuma
   * wiki — and the collision was a duplicate key in this object that the compiler caught.
   */
  commerceProducts: "product:read",
  /**
   * COM-03's per-SKU count, which is the stock column and one request per row.
   *
   * EXP-002's `cart-reviews` holds this scope on the rider's behalf for the cart; nothing composes
   * a product list, so this application holds it directly for `Products · stock`.
   */
  inventory: "inventory:read",
  /** COM-04's write, which is the quantity stepper and the source of the designed 409. */
  cart: "cart:write",
  /**
   * COM-05's order command, which is the one button `Checkout` exists to press.
   *
   * New with that screen, and it is a **write** rather than a read because placing an order mints
   * an immutable priced snapshot. `channel-scopes.mjs` derives the rider client's grant from the
   * literals in this file, so naming it here is what makes the realm able to grant it — the realm
   * file is regenerated and needs applying before a signed-in rider can place an order.
   */
  placeOrder: "commerce-order:write",
  /** COM-07's preparation, which is `Order tracking`'s step bar. */
  orderPreparation: "order-preparation:read",
  /**
   * SUP-01's case reads, and SUP-02's timeline is bounded by its own data rather than by a scope.
   *
   * One scope for both services' customer-facing reads would be wrong; this is SUP-01's, and
   * `support-timeline:read` below is SUP-02's. Neither is an agent scope: `support-cases:agent`
   * and `support-timeline:agent-read` are what widen a reader past their own cases, and no rider
   * client is granted either.
   */
  supportCases: "support-cases:read",
  /** SUP-02's timeline, refused per entry to a subject who is not a participant. */
  supportTimeline: "support-timeline:read",
  /** FIN-04's refund, which is the whole of `Refund`. */
  refunds: "refund:read",
  /** CUS-04's consent history, which is the four switches and their version. */
  consentRead: "customer:consent:read",
  /** CUS-04's grant and withdrawal, which is the switch moving. */
  consentWrite: "customer:consent:write",
  /** CUS-03's preferences, which are three of `Settings`'s rows. */
  preferences: "customer-preferences:read",
  /** IAM-04's session listing, which is `Signed-in devices`. */
  sessionRead: "session:read",
  /** IAM-04's revocation, which signs one other device out. */
  sessionRevoke: "session:revoke",

  /* ------------------------------------ the third tranche: a trip with more than one stop */

  /**
   * TRP-04's plan read, which is `Multi-stop in trip`'s subject.
   *
   * `trip:stops:read` rather than `trip:stops:rider:manage` or `trip:stops:driver:transition`:
   * this screen reads a plan in progress and changes nothing about it. The design's own note says
   * stops can be reordered **until pickup** and that after that a rider asks the driver, so the
   * screen that draws a trip already running holds neither write.
   */
  stopPlan: "trip:stops:read",
  /**
   * GEO-02's address read, which turns `plan.stops.addressId` into a place name.
   *
   * The scope [ADR 0035](../../../docs/adr/0035-an-address-is-a-record-geo-02-owns-and-re-resolves.md)
   * published and ADR 0036 made spendable from a stop. Until this screen was drawn no channel client
   * was granted it — `channel-scopes.mjs` derives each client's grant from its own application's
   * AST, and nothing in any application named it. This literal is what changes that.
   */
  address: "address:read",

  /* ------------------------------------------------- the fifth tranche: money, and how it is paid */

  /**
   * FIN-01's own wallet read and its top-up command, under
   * [ADR 0041](../../../docs/adr/0041-a-riders-own-wallet-and-the-balance-projection-behind-it-are-published-under-v1.md).
   *
   * `wallet:self` rather than `wallet:read`, and the difference is the whole of that ADR's §3. A
   * caller holding `wallet:read` may read **every wallet in its tenant**, the platform's own
   * included; `wallet:self` reads the one whose `ownerId` equals the verified token subject and
   * answers the same 404 as an absent wallet for anything else. One scope covers the read and the
   * command, which is `AGENTS.md`'s Subject scoping rule.
   */
  wallet: "wallet:self",

  /**
   * FIN-03's balance projection, under the same ADR, and the one scope on this list whose name is
   * deliberately **not** `:self`.
   *
   * `balance:projection:read` is tenant-bounded and says so. FIN-03's projection row carries
   * `(tenantId, accountId, currency)` and no subject, so there is nothing on it to compare a token
   * against — ADR 0031's position for DSP-04, taken again. Naming it `balance:self` would have put a
   * bound in the vocabulary that no code enforces, which is worse than the unbounded read because
   * the next reader would believe it. Q-145 carries the fix.
   */
  balanceProjection: "balance:projection:read",

  /**
   * FIN-02's journal transactions, which are the `Recent` rows on both wallet screens.
   *
   * The listing answers every posting in the tenant and offers no customer or account filter, so
   * this scope buys a page this application has to narrow itself. `Wallet.tsx` records that where it
   * happens; it is the same finding `Refund.tsx` records about `GET /v1/refunds`, one domain over.
   */
  ledger: "ledger:read",

  /**
   * PAY-04's own instruments, under
   * [ADR 0042](../../../docs/adr/0042-a-masked-mobile-money-instrument-is-pay-04s-record-and-only-its-mask-is-published.md).
   *
   * One scope for the listing and the registration, and `:self` is earned here: `owner_subject` is
   * on the row and is compared in the repository's own predicate. The two execution scopes —
   * `mobile-money:execute` and `mobile-money:reconcile` — are deliberately **not** on this list and
   * must never be: `POST …/mobile-money/executions` moves money.
   */
  paymentInstruments: "mobile-money:instruments:self",
} as const;
